import { Component, computed, effect, HostListener, inject, signal } from '@angular/core';
import { WorkflowService } from '../../service/app-state/workflow.service';
import {
  AppViewType,
  ExtractionView,
  isAuftragAssignmentView,
  isConsolidationView,
  isDocumentView,
  isErfasseBehandlerView,
  isExtractionView,
  isTableRowClassificationView,
} from '../../models/app-view';
import { ExtractionPageComponent } from '../extraction-page/extraction-page.component';
import { RowClassificationPageComponent } from '../position-classification-page/row-classification-page.component';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { Ct2DocumentPreviewComponent } from '../../domains/preview/ct2-document-preview.component';
import {
  ExtractionPage,
  ExtractionSectionViewType,
  isTableModel,
} from '../../models/domain-extraction-specifications';
import { ConsolidationComponent } from '../consolidation-page/consolidation.component';
import { ConsolidationService } from '../../service/consolidation.service';
import {
  consolidationSections,
  ct2ConfigurationByBatchDomain,
  tableSectionsByDomain,
} from '../../models/extraction-models/extraction-models';
import { ToolbarService } from '../../service/app-state/toolbar.service';
import { Proof301OverviewService } from '../301-warning/proof301-overview.service';
import { Existing301WarningComponent } from '../301-warning/existing-301-warning.component';
import { CurrentFieldService } from '../../service/current-field.service';
import { BatchType, CtBatchSource } from '@ps-gui/claims-terminal-shared';
import { ErfassungBehandlerComponent } from '../erfassung-behandler-page/erfassung-behandler.component';
import { ErfassungBehandlerSections } from '../erfassung-behandler-page/erfassung-behandler-field-group-model';
import { AuftragAssignmentComponent } from '../auftrag-assignment/auftrag-assignment.component';
import { DevToolsCardComponent } from '@ps-gui/ui';

@Component({
  selector: 'ct-extraction-app-view-page',
  templateUrl: './app-view-page.component.html',
  imports: [
    ExtractionPageComponent,
    RowClassificationPageComponent,
    Ct2DocumentPreviewComponent,
    ConsolidationComponent,
    DevToolsCardComponent,
    Existing301WarningComponent,
    ErfassungBehandlerComponent,
    AuftragAssignmentComponent,
  ],
})
export class AppViewPageComponent {
  protected readonly AppViewType = AppViewType;
  protected readonly isErfasseBehandlerView = isErfasseBehandlerView;

  protected readonly workflowService = inject(WorkflowService);
  protected readonly ctBatchStoreService = inject(CtBatchStoreService);
  protected readonly consolidationService = inject(ConsolidationService);
  protected readonly currentFieldService = inject(CurrentFieldService);

  // TODO: Realize this in a more generic way when more warnings are required
  protected readonly proof301OverviewService = inject(Proof301OverviewService);

  private readonly toolbarService = inject(ToolbarService);

  protected readonly currentDocument = computed(() => {
    const view = this.workflowService.currentView();
    if (!isDocumentView(view)) return undefined;
    return this.ctBatchStoreService.getDocumentById(view.documentId);
  });

  protected readonly documents = computed(() => {
    const view = this.workflowService.currentView();

    if (isErfasseBehandlerView(view)) {
      return this.ctBatchStoreService.ctBatch.documents;
    } else if (isConsolidationView(view)) {
      return this.consolidationService.getConsolidationDocumentsBySplitDocumentId(
        view.splitDocumentId,
      );
    } else {
      const currentDocument = this.currentDocument();
      return currentDocument ? [currentDocument] : this.ctBatchStoreService.ctBatch.documents;
    }
  });

  protected readonly isExtractionPageWithTableSection = computed(() => {
    const view = this.workflowService.currentView();
    return (
      (isExtractionView(view) &&
        view.page.sections.filter((section) => isTableModel(section)).length === 1) ||
      (isConsolidationView(view) &&
        tableSectionsByDomain[this.ctBatchStoreService.ctBatchDomain].length > 0)
    );
  });

  protected readonly hasMultipleTableSections = computed(() => {
    const view = this.workflowService.currentView();
    return (
      isExtractionView(view) &&
      view.page.sections.filter((section) => isTableModel(section)).length > 1
    );
  });

  protected readonly enableOverlay = signal(true);
  protected readonly selectedDocumentId = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      if (!this.isExtractionPageWithTableSection()) {
        this.toolbarService.setTableCreationEnabled(false);
      }
    });

    effect(() => {
      const view = this.workflowService.currentView();
      this.currentFieldService.currentView = view;

      if (!isErfasseBehandlerView(view)) {
        this.enableOverlay.set(true);
      }

      if (isAuftragAssignmentView(view)) {
        const consolidationSections =
          ct2ConfigurationByBatchDomain[BatchType.PROOF].consolidation.sections;

        this.currentFieldService.extractionPage = {
          sections: consolidationSections,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        };
        this.currentFieldService.currentActiveSection = consolidationSections[0];
        return;
      }

      if (isErfasseBehandlerView(view)) {
        this.currentFieldService.extractionPage = {
          sections: ErfassungBehandlerSections,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        };
        this.currentFieldService.currentActiveSection = ErfassungBehandlerSections[0];
        return;
      }

      if (isTableRowClassificationView(view)) {
        this.currentFieldService.extractionPage = undefined;
        return;
      }

      if (isExtractionView(view)) {
        this.currentFieldService.extractionPage = (view as ExtractionView).page;
        return;
      }

      if (isConsolidationView(view)) {
        const sections = consolidationSections(this.ctBatchStoreService.ctBatchDomain);

        this.currentFieldService.extractionPage = {
          sections: sections,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        } as ExtractionPage;

        if (this.ctBatchStoreService.ctBachSource === CtBatchSource.LEGACY) {
          this.currentFieldService.currentActiveSection = sections[0];
        }

        return;
      }
    });
  }

  onContinue() {
    this.workflowService.navigateToNextView();
  }

  // we need this to trigger the first field after a full page reload
  @HostListener('window:keydown.enter', ['$event'])
  preventBrowserSubmit(event: Event) {
    event.preventDefault();
    if (document.activeElement === document.body) {
      this.currentFieldService.focusNextField();
    }
  }
}
